// src/pages/TerritoriesPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import { useTheme } from "@mui/material/styles";
import { FileUp } from "lucide-react";
import GreenGlowOutlineButton from "../components/GreenGlowOutlineButton";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import TerritoryQuickCreateModal from "../components/TerritoryQuickCreateModal";

function UploadLeadsRowButton({ onClick, fullWidth = false }) {
  return (
    <GreenGlowOutlineButton
      data-tour="upload-leads-chip"
      fullWidth={fullWidth}
      onClick={onClick}
      startIcon={<FileUp size={16} strokeWidth={2.25} />}
    >
      Upload Leads
    </GreenGlowOutlineButton>
  );
}

export default function TerritoriesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { can, isReadOnly } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignedReps, setAssignedReps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({ name: "", manager: "", assigned_reps: [] });
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openQuickCreate, setOpenQuickCreate] = useState(false);
  const [loading, setLoading] = useState(true);



const mobileTerritoryCardSx = {
  p: 2.25,
  borderRadius: 3,

  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(30,41,59,0.88)"
      : "background.paper",

  border: "1px solid",
  borderColor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",

  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 8px 24px rgba(0,0,0,0.55)"
      : "0 4px 12px rgba(0,0,0,0.12)",

  cursor: "pointer",

  "&:active": {
    transform: "scale(0.995)",
  },
};


  // 🔹 ----------------------------------------------------Fetch territories and users
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [territoryRes, userRes] = await Promise.all([
        API.get("/territories/"),
        API.get("/users/"),
      ]);
      setTerritories(territoryRes.data);
      setUsers(userRes.data);
      console.log("✅ Territories API response:", territoryRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load territories or users");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 ---------------------------------------------------When row clicked → open modal and load values
  const handleRowClick = (params) => {
    const rowData = params?.row;
    if (!rowData) return;
    navigate(`/territory/add/lead?territory_id=${rowData.id}`);

    console.log("🟢 Territory clicked:", rowData);

    setSelected(rowData);
    setFormData({
      name: rowData.name || "",
      manager: rowData.manager?.id || "", // ✅ ensure numeric ID
      assigned_reps: rowData.assigned_reps?.map((rep) => rep.id) || [],
    });

    setOpen(false);
    setSelected(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      const payload = {
        name: formData.name,
        manager_id: formData.manager || null, // ✅ use correct key
        assigned_reps_ids: formData.assigned_reps || [],
      };

      await API.put(`/territories/${selected.id}/`, payload);
      toast.success("Territory updated");
      await fetchData();
      setOpen(false);
    } catch (err) {
      console.error("Error updating territory:", err);
      toast.error("Failed to update territory");
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`/territories/${selected.id}/`);
      toast.success("🗑️ Territory deleted");
      await fetchData();
      setOpen(false);
    } catch (err) {
      console.error("Error deleting territory:", err);
      toast.error("Failed to delete territory");
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleAddLeads = () => {
    if (!selected?.id) return toast.warn("⚠️ Select a territory first.");
    setOpen(false);
    navigate(`/territory/add/lead?territory_id=${selected.id}`);
  };

  // Handle edit icon click - navigate to add leads page
  const handleEditClick = (e, territory) => {
    e.stopPropagation(); // Prevent row click
    navigate(`/territory/add/lead?territory_id=${territory.id}`);
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {/* Header Card */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(25,118,210,0.08)",
              }}
            >
              <PushPinIcon color="primary" />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Territories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create, manage your territories, upload and assign leads. Click on any row to manage current territory. ({territories.length} {territories.length === 1 ? "territory" : "territories"})
              </Typography>
            </Box>
          </Stack>

          <Button
            data-tour="add-territory-button"
            variant="contained"
            onClick={() => setOpenQuickCreate(true)}
            sx={{
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.875rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.4,
              padding: "8px 16px",
              whiteSpace: "nowrap",
              width: { xs: "100%", md: "auto" },
              mt: { xs: 2, md: 0 },
              transition: "all 0.18s ease",
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "#fff",
              boxShadow: (theme) =>
                `0 10px 22px ${theme.palette.primary.main}47`,
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: (theme) =>
                  `0 14px 28px ${theme.palette.primary.main}61`,
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: (theme) =>
                  `0 6px 14px ${theme.palette.primary.main}40`,
              },
            }}
          >
            + Add Territory
          </Button>
        </Stack>
      </Card>

      <TerritoryQuickCreateModal
        open={openQuickCreate}
        onClose={() => setOpenQuickCreate(false)}
        onCreated={fetchData}
        isReadOnly={isReadOnly}
      />

      {/* ------------------------------------------------------------TABLE CARD - Material Dashboard Style */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          overflow: "visible",
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
              {isMobile ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  px: { xs: 0.5, sm: 2 },
                  py: { xs: 1, sm: 2 },
                }}
              >
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            ) : (
              <Box sx={{ px: 2, py: 1 }}>
                <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            )}
          </Box>
        ) : territories.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" gutterBottom>No territories yet.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create a territory to assign leads and reps.</Typography>
            {!isReadOnly && (
              <Button variant="contained" color="primary" onClick={() => navigate("/territory/add")}>
                Create a territory
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                mb: 2,
                px: 2,
                pt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {territories.length} {territories.length === 1 ? "territory" : "territories"}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", overflow: "auto" }}>
              {isMobile ? (
                // -------------------------------------------------------------------------------------Mobile Card View
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 1, sm: 2 },
                  }}
                >
                  {territories.map((territory) => (
                    <Card
  key={territory.id}
  onClick={() => handleRowClick({ row: territory })}
  sx={mobileTerritoryCardSx}
>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/*------------------------------------------------------------------------------ Territory Name */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                            Territory Name
                          </Typography>
                          <Typography
  variant="body1"
  fontWeight={600}
  sx={{ color: "primary.main" }}
>
  {territory.name}
</Typography>

                 
                        </Box>

                        {/* ----------------------------------------------------------------------------------Manager */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                            Manager
                          </Typography>
                          <Typography variant="body2">
                            {territory.manager_name || "—"}
                          </Typography>
                        </Box>

                        {/* ---------------------------------------------------------------Leads Count */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: "block" }}>
                            Leads Count
                          </Typography>
                          <Typography variant="body2">
                            {territory.leads_count ?? territory.lead_count ?? "0"}
                          </Typography>
                        </Box>

                        {/* ---------------------------------------------------------------------Action */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                          <UploadLeadsRowButton
                            fullWidth
                            onClick={(e) => handleEditClick(e, territory)}
                          />
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : (
                // --------------------------------------------------------------------------------Desktop Table View
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "grey.50",
                      }}
                    >
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Territory Name
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Manager
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                        Leads Count
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", textAlign: "right" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {territories.map((territory) => (
                      <TableRow
                        key={territory.id}
                        hover
                        onClick={() => handleRowClick({ row: territory })}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{territory.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {territory.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {territory.manager_name || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {territory.leads_count ?? territory.lead_count ?? "0"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <UploadLeadsRowButton
                            onClick={(e) => handleEditClick(e, territory)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </>
        )}
      </Card>

      {/* 🔹 -------------------------------------------------------------------------------Edit Territory Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: theme.palette.background.paper,
            boxShadow: 24,
            borderRadius: 2,
            p: 5,
            width: 500,
            maxWidth: "90%",
            minHeight: 420,
          }}
        >
          <Typography variant="h6" mb={3}>
            Edit Territory
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Territory Name"
              name="name"
              fullWidth
              value={formData.name || ""}
              onChange={handleChange}
            />

            <FormControl fullWidth>
              <InputLabel>Manager</InputLabel>
              <Select
                name="manager"
                label="Manager"
                value={formData.manager || ""}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
  <InputLabel>Assigned Reps</InputLabel>
  <Select
    multiple
    value={assignedReps}
    label="Assigned Reps"
    onChange={(e) => setAssignedReps(e.target.value)}
    renderValue={(selected) =>
      users
        .filter((u) => selected.includes(u.id))
        .map((u) => `${u.first_name} ${u.last_name}`)
        .join(", ")
    }
  >
    {users.map((u) => (
      <MenuItem key={u.id} value={u.id}>
        <span>{u.first_name} {u.last_name}</span>
      </MenuItem>
    ))}
  </Select>
</FormControl>


            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              mt={4}
            >
              {can.canDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  sx={{ flex: 1 }}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{ flex: 1 }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAddLeads}
                sx={{ flex: 1 }}
              >
                + Add Leads
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Territory"
        message="Are you sure you want to delete this territory? All associated leads will be unassigned. This action cannot be undone."
        itemName={selected?.name ? `Territory: ${selected.name}` : null}
      />
    </Box>
</Box>
  );
}
